import { motion } from "framer-motion";
import { useState } from "react";
import { Play, BookOpen, Video, FileText } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { usePlayer } from "@/context/PlayerContext";
import { fileUrl } from "@/lib/api";
import { MediaDialog } from "@/components/MediaView";
import { useSite } from "@/context/SiteContext";

export const SHAPES = [["rectangle", "Rectangle"], ["rounded", "Rounded rectangle"], ["arch", "Arch"], ["circle", "Circle"]];
const SHAPE_CLASS = { rectangle: "aspect-[4/5] rounded-none", rounded: "aspect-[4/5] rounded-t-[28px]", arch: "aspect-[4/5] rounded-t-[999px]", circle: "mx-auto mt-4 aspect-square !w-[86%] rounded-full" };

const isImg = (f) => (f?.type || "").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(f?.name || "");

// File-only messages look like a real document page instead of a music player.
const DocCover = ({ s, files, label }) => {
  const preview = s.cover_url || files.find(isImg)?.url;
  const first = files[0];
  return (
    <a href={fileUrl(first.url)} target="_blank" rel="noopener noreferrer" data-testid={`sermon-doc-open-${s.id}`} aria-label={`Open ${first.name || "document"}`}
      className="relative block bg-[#EFE8DA] px-6 pb-6 pt-8 sm:px-8">
      <span className="absolute left-4 top-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#8A6A12]">{label}</span>
      <div className="relative mx-auto aspect-[1/1.414] w-full max-w-[300px] overflow-hidden rounded-[4px] bg-white shadow-[0_1px_2px_rgba(20,33,61,0.08),0_14px_30px_-12px_rgba(20,33,61,0.35)] transition-transform duration-500 group-hover:-translate-y-1">
        {preview ? (
          <img src={fileUrl(preview)} alt="" loading="lazy" className="h-full w-full object-contain object-top p-2" />
        ) : (
          <div className="flex h-full flex-col p-6">
            <FileText className="h-10 w-10 text-[#B4442A]" />
            <p className="mt-4 line-clamp-3 break-words font-ui text-sm font-semibold text-[#14213D]">{first.name || "Document"}</p>
            <div className="mt-5 grid gap-2">{[92, 100, 84, 96, 70, 88, 60].map((w, i) => <span key={i} style={{ width: `${w}%` }} className="h-1.5 rounded bg-[#14213D]/10" />)}</div>
          </div>
        )}
        <span className="absolute right-0 top-0 h-7 w-7 bg-gradient-to-bl from-[#EFE8DA] from-50% to-[#D9D0BE] to-50%" />
      </div>
      <span className="mx-auto mt-4 flex w-fit items-center gap-2 rounded-full bg-[#14213D] px-4 py-2 font-ui text-xs font-semibold text-white"><FileText className="h-3.5 w-3.5" /> Open document</span>
    </a>
  );
};

export const SermonCard = ({ s, index = 0 }) => {
  const { t, pick } = useLang();
  const { playSermon, track, playing } = usePlayer();
  const active = track?.id === s.id && playing;
  const [watch, setWatch] = useState(false);
  const { settings } = useSite();
  const details = settings?.sermon_details === true;
  const shape = SHAPE_CLASS[settings?.sermon_shape] || SHAPE_CLASS.rounded;
  const Cover = details ? "button" : "div";
  const files = Array.isArray(s.attachments) ? s.attachments : [];
  const isDoc = !s.audio_url && !s.video_url && files.length > 0;
  const onPlay = () => {
    if (s.audio_url) return playSermon(s);
    if (s.video_url) return setWatch(true);
    return playSermon(s);
  };
  return (
    <motion.article
      data-testid={`sermon-card-${s.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: (index % 3) * 0.08 }}
      className="group flex flex-col"
    >
      {isDoc ? <DocCover s={s} files={files} label={t(`cat.${s.category}`)} /> : (
      <Cover
        onClick={details ? onPlay : undefined}
        data-testid={details ? `sermon-play-btn-${s.id}` : `sermon-cover-${s.id}`}
        data-shape={settings?.sermon_shape || "rounded"}
        className={`relative block w-full overflow-hidden bg-[#131C2E] text-left ${shape}`}
        aria-label={details ? `Play ${s.title_en}` : undefined}
      >
        {s.cover_url && (
          <img src={fileUrl(s.cover_url)} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.07]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B111E] via-[#0B111E]/30 to-transparent" />
        <span className={`absolute top-[42%] rounded-full bg-[#0B111E]/70 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] backdrop-blur-md ${settings?.sermon_shape === "circle" ? "left-1/2 -translate-x-1/2" : "left-5"}`}>{t(`cat.${s.category}`)}</span>
        {details && (
          <>
            <span className={`absolute bottom-5 right-5 grid h-14 w-14 place-items-center rounded-full transition-[background-color,transform] duration-500 group-hover:scale-110 ${active ? "bg-[#E07A5F]" : "bg-[#D4AF37]"} text-[#0B111E]`}>
              <Play className="h-5 w-5 translate-x-[1px]" />
            </span>
            <span data-testid={`sermon-duration-${s.id}`} className="absolute bottom-6 left-5 font-mono text-xs text-[#F4EEE2]/85">{s.duration}</span>
          </>
        )}
      </Cover>
      )}
      <div className={`flex flex-1 flex-col border-x border-b border-[#F4EEE2]/10 p-5 ${isDoc ? "border-t" : ""}`}>
        <h3 data-ve-rec={`sermons|${s.id}|title_`} className="font-display text-2xl font-semibold leading-tight text-[#F4EEE2]">{pick(s.title_en, s.title_mh)}</h3>
        <p data-ve-rec={`sermons|${s.id}|description_`} className="mt-2 line-clamp-2 text-sm leading-relaxed text-[#B7BFCC]">{pick(s.description_en, s.description_mh)}</p>
        {s.video_url && (
          <button data-testid={`sermon-watch-btn-${s.id}`} onClick={() => setWatch(true)} className="mt-4 flex w-fit items-center gap-2 rounded-full border border-[#D4AF37]/50 px-4 py-2 font-ui text-xs font-semibold text-[#F4EEE2] transition-colors hover:bg-[#D4AF37]/10">
            <Video className="h-3.5 w-3.5 text-[#D4AF37]" /> {t("sermons.watch")}
          </button>
        )}
        <div className="mt-auto flex items-center justify-between pt-5 font-ui text-xs text-[#B7BFCC]">
          <span data-ve-rec={`sermons|${s.id}|speaker`}>{s.speaker}</span>
          {s.scripture && (
            <span className="flex items-center gap-1.5 text-[#D4AF37]">
              <BookOpen className="h-3.5 w-3.5" /> <span data-ve-rec={`sermons|${s.id}|scripture`}>{s.scripture}</span>
            </span>
          )}
        </div>
        {files.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {files.map((a, i) => (
              <a key={a.url} href={fileUrl(a.url)} target="_blank" rel="noopener noreferrer" data-testid={`sermon-file-${s.id}-${i}`} className="flex max-w-full items-center gap-1.5 rounded-full bg-[#D4AF37]/10 px-3 py-1.5 font-ui text-xs text-[#F4EEE2] transition-colors hover:bg-[#D4AF37]/20">
                <FileText className="h-3.5 w-3.5 shrink-0 text-[#D4AF37]" /><span className="truncate">{a.name || "File"}</span>
              </a>
            ))}
          </div>
        )}
      </div>
      <MediaDialog item={watch ? { kind: "video", url: s.video_url } : null} onClose={() => setWatch(false)} title={pick(s.title_en, s.title_mh)} caption={s.speaker} />
    </motion.article>
  );
};
